import { collection, doc, getDoc, getDocs, limit, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase.config';

export type InsightPostRef = {
  id: string;
  slug?: string;
};

/** URL segment for /insights/:segment (slug when set, else Firestore id for legacy posts). */
export function insightArticlePathSegment(post: InsightPostRef): string {
  const s = post.slug?.trim();
  if (s) return s;
  return post.id;
}

export function insightArticleHref(post: InsightPostRef): string {
  return `/insights/${encodeURIComponent(insightArticlePathSegment(post))}`;
}

/**
 * Public list links: use slug when it is unique among `peers` (same loaded batch).
 * If several posts share the same slug (bad Firestore data), use document id so each
 * card opens the correct article instead of converging on one URL.
 */
export function insightArticleHrefInContext(post: InsightPostRef, peers: InsightPostRef[]): string {
  const s = typeof post.slug === 'string' ? post.slug.trim() : '';
  if (!s) return `/insights/${encodeURIComponent(post.id)}`;
  const same = peers.filter((p) => (typeof p.slug === 'string' ? p.slug.trim() : '') === s);
  if (same.length > 1) return `/insights/${encodeURIComponent(post.id)}`;
  return `/insights/${encodeURIComponent(s)}`;
}

/**
 * When multiple published posts share the same `slug`, keep the oldest (by `date`)
 * on that slug and assign new unique slugs to the rest. Call from admin only
 * (requires `request.auth` for blogPosts updates beyond guest slug-only rules).
 */
export async function repairDuplicateInsightSlugsInFirestore(
  posts: InsightBlogPost[]
): Promise<InsightBlogPost[]> {
  const bySlug = new Map<string, InsightBlogPost[]>();
  for (const p of posts) {
    const s = typeof p.slug === 'string' ? p.slug.trim() : '';
    if (!s) continue;
    const arr = bySlug.get(s) || [];
    arr.push(p);
    bySlug.set(s, arr);
  }
  let out = [...posts];
  for (const [, group] of Array.from(bySlug.entries())) {
    if (group.length < 2) continue;
    const sorted = [...group].sort((a, b) => {
      const ta = a.date?.toDate?.()?.getTime() ?? 0;
      const tb = b.date?.toDate?.()?.getTime() ?? 0;
      return ta - tb;
    });
    const [, ...dupes] = sorted;
    for (const p of dupes) {
      const title = typeof p.title === 'string' ? p.title.trim() : 'article';
      const tail = p.id.length >= 6 ? p.id.slice(-6) : p.id;
      try {
        const newSlug = await ensureUniqueInsightSlug(`${title}-${tail}`, p.id);
        await updateDoc(doc(db, 'blogPosts', p.id), { slug: newSlug });
        out = out.map((r) => (r.id === p.id ? { ...r, slug: newSlug } : r));
      } catch (e) {
        console.warn('repairDuplicateInsightSlugsInFirestore', p.id, e);
      }
    }
  }
  return out;
}

/**
 * Normalise title (or manual path) into a readable URL segment: Unicode letters/numbers, hyphens, max length.
 */
export function slugifyInsightSegment(raw: string): string {
  const s = raw
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 96);
  return s || 'article';
}

/**
 * Resolves a unique `slug` value among `blogPosts` (excluding `excludeDocId` on edit).
 */
export async function ensureUniqueInsightSlug(base: string, excludeDocId?: string): Promise<string> {
  const root = slugifyInsightSegment(base);
  let candidate = root;
  let n = 0;
  for (;;) {
    const snap = await getDocs(query(collection(db, 'blogPosts'), where('slug', '==', candidate)));
    const taken = snap.docs.some((d) => d.id !== excludeDocId);
    if (!taken) return candidate;
    n += 1;
    candidate = `${root}-${n}`;
  }
}

/**
 * Writes a title-derived `slug` when missing so public URLs use /insights/my-article-title
 * instead of the Firestore document id. Safe for anonymous clients only when rules allow slug-only first write.
 */
export async function persistSlugForPublishedPostIfMissing(post: InsightBlogPost): Promise<InsightBlogPost> {
  const title = typeof post.title === 'string' ? post.title.trim() : '';
  const existing = typeof post.slug === 'string' ? post.slug.trim() : '';
  if (existing || !title) return post;
  try {
    const slug = await ensureUniqueInsightSlug(title, post.id);
    await updateDoc(doc(db, 'blogPosts', post.id), { slug });
    return { ...post, slug };
  } catch (e) {
    console.warn('persistSlugForPublishedPostIfMissing', post.id, e);
    return post;
  }
}

export interface InsightBlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  date: { toDate?: () => Date } | null;
  published: boolean;
  imageURL?: string;
  slug?: string;
}

/** Load a single published post by URL segment: slug match first, then document id (legacy). */
export async function fetchPublishedInsightBySegment(segment: string): Promise<InsightBlogPost | null> {
  const decoded = decodeURIComponent(segment || '').trim();
  if (!decoded) return null;

  const bySlug = query(
    collection(db, 'blogPosts'),
    where('slug', '==', decoded),
    where('published', '==', true),
    limit(1)
  );
  const slugSnap = await getDocs(bySlug);
  if (!slugSnap.empty) {
    const d = slugSnap.docs[0];
    const post = { id: d.id, ...d.data() } as InsightBlogPost;
    return persistSlugForPublishedPostIfMissing(post);
  }

  const ref = doc(db, 'blogPosts', decoded);
  const snap = await getDoc(ref);
  if (snap.exists() && snap.data()?.published === true) {
    const post = { id: snap.id, ...snap.data() } as InsightBlogPost;
    return persistSlugForPublishedPostIfMissing(post);
  }
  return null;
}
